/* Kleine Datumshelfer. Bewusst ohne Bibliothek. */

const WOCHENTAGE = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

const MONATE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

export function heute() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function plusTage(datum, tage) {
  const d = new Date(datum);
  d.setDate(d.getDate() + tage);
  return d;
}

/*
  Nächster Wochentag (0 = Sonntag … 6 = Samstag), der mindestens
  `mindestensVoraus` Tage in der Zukunft liegt.
*/
export function naechsterWochentag(basis, wochentag, mindestensVoraus = 1) {
  const d = plusTage(basis, mindestensVoraus);
  while (d.getDay() !== wochentag) {
    d.setDate(d.getDate() + 1);
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

export function mitUhrzeit(datum, stunde, minute) {
  const d = new Date(datum);
  d.setHours(stunde, minute, 0, 0);
  return d;
}

export function wochentag(datum) {
  return WOCHENTAGE[new Date(datum).getDay()];
}

/* „Donnerstag, 30. Juli 2026“ */
export function langesDatum(datum) {
  const d = new Date(datum);
  return `${WOCHENTAGE[d.getDay()]}, ${d.getDate()}. ${MONATE[d.getMonth()]} ${d.getFullYear()}`;
}

/* „30.07.2026“ */
export function kurzesDatum(datum) {
  const d = new Date(datum);
  const tag = String(d.getDate()).padStart(2, "0");
  const monat = String(d.getMonth() + 1).padStart(2, "0");
  return `${tag}.${monat}.${d.getFullYear()}`;
}

/* „18:00 Uhr“ */
export function uhrzeit(datum) {
  const d = new Date(datum);
  const std = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${std}:${min} Uhr`;
}

export function tageBis(datum) {
  const ziel = new Date(datum);
  ziel.setHours(0, 0, 0, 0);
  return Math.round((ziel - heute()) / 86400000);
}

/* „morgen“, „in 4 Tagen“, „überfällig seit gestern“ */
export function fristInWorten(datum) {
  const tage = tageBis(datum);
  if (tage < -1) return `überfällig seit ${Math.abs(tage)} Tagen`;
  if (tage === -1) return "überfällig seit gestern";
  if (tage === 0) return "heute fällig";
  if (tage === 1) return "morgen fällig";
  return `in ${tage} Tagen fällig`;
}

/* „Donnerstag, 18:00 Uhr“ — für den Sitzungsrhythmus */
export function tagUndUhrzeit(datum) {
  return `${wochentag(datum)}, ${uhrzeit(datum)}`;
}

/* ---------------------------------------------------------------
   Monatsraster für den Kalender
   --------------------------------------------------------------- */

export function monatsname(datum) {
  const d = new Date(datum);
  return `${MONATE[d.getMonth()]} ${d.getFullYear()}`;
}

export function monatVerschoben(datum, um) {
  const d = new Date(datum);
  /*
    Erst auf den Ersten, dann den Monat wechseln. Sonst wird aus dem
    31. Januar plus einem Monat der 3. März — der Februar hat den 31. nicht,
    und JavaScript rechnet dann stillschweigend weiter.

    Nachgerechnet und in `datum.test.js` festgehalten: Auch der 31. März
    minus einem Monat landet naiv auf dem 3. März. Wer im Kalender blättert,
    übersähe einen ganzen Monat.
  */
  d.setDate(1);
  d.setMonth(d.getMonth() + um);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function gleicherTag(a, b) {
  const x = new Date(a);
  const y = new Date(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/*
  Die sechs Wochen eines Monatsblatts, Montag bis Sonntag.

  Immer sechs Zeilen, auch wenn fünf reichten: Ein Blatt, das je nach Monat
  die Höhe wechselt, lässt beim Blättern alles darunter springen.

  Die Tage vor und nach dem Monat kommen mit — ausgegraut, aber vorhanden.
  Ein Kalender, der am 1. mit einer Lücke beginnt, ist schwerer zu lesen als
  einer, der die Woche vollständig zeigt.
*/
export function monatsraster(monat) {
  const erster = new Date(monat);
  erster.setDate(1);
  erster.setHours(0, 0, 0, 0);

  /* getDay: 0 = Sonntag. Wir wollen Montag als ersten Tag der Woche. */
  const versatz = (erster.getDay() + 6) % 7;

  const start = plusTage(erster, -versatz);
  const wochen = [];

  for (let woche = 0; woche < 6; woche += 1) {
    const tage = [];
    for (let tag = 0; tag < 7; tag += 1) {
      const d = plusTage(start, woche * 7 + tag);
      d.setHours(0, 0, 0, 0);
      tage.push({ datum: d, imMonat: d.getMonth() === erster.getMonth() });
    }
    wochen.push(tage);
  }

  return wochen;
}

/* ---------------------------------------------------------------
   Wie lange ist das her?
   --------------------------------------------------------------- */

/*
  „vor 2 Stunden“ statt „02.08.2026, 09:14 Uhr“.

  Das Gegenstück zu `fristInWorten`, das nach vorn schaut. Gebraucht wird es
  in Office: Dokumente stehen dort nach der letzten Änderung, und wer sucht,
  erinnert sich an „gestern“ — nicht an ein Datum.

  Ab einer Woche steht wieder das Datum. „vor 23 Tagen“ sagt niemandem
  etwas; „12.07.2026“ schon.
*/
export function vorWieLange(datum, jetzt = new Date()) {
  /*
    `null` ist KEIN ungültiges Datum.

    `new Date(null)` ergibt den 1. Januar 1970 — eine völlig gültige Zahl,
    an der `isNaN` vorbeigeht. Ein Dokument ohne Änderungsdatum stünde in
    Office also mit „01.01.1970“ da. Deshalb werden leer, `null` und
    `undefined` vorher abgefangen und nicht erst dem `Date` überlassen.
  */
  if (datum === null || datum === undefined || datum === "") return "";

  const d = datum instanceof Date ? datum : new Date(datum);
  if (isNaN(d.getTime())) return "";

  const sekunden = Math.floor((jetzt.getTime() - d.getTime()) / 1000);

  /*
    Uhren gehen auseinander — die des Servers und die des Browsers. Ein
    Dokument, das „in 3 Sekunden“ geändert wurde, wäre eine sinnlose
    Meldung; deshalb gilt alles knapp in der Zukunft als eben passiert.
  */
  if (sekunden < 60) return "gerade eben";

  const minuten = Math.floor(sekunden / 60);
  if (minuten < 60) return minuten === 1 ? "vor einer Minute" : `vor ${minuten} Minuten`;

  /*
    Ab hier wird nach KALENDERTAGEN gerechnet, nicht nach 24-Stunden-Blöcken.

    ---------------------------------------------------------------
    Warum der Kalender vor den Stunden kommt
    ---------------------------------------------------------------

    Wer abends um 23 Uhr schreibt und morgens um 7 nachsieht, erwartet
    „gestern“. Nach Stunden gerechnet wären das acht — und „vor 8 Stunden“
    ist zwar richtig, taugt aber nicht zum Wiederfinden. Genau darum geht es
    bei dieser Funktion: Man erinnert sich an „gestern“, nicht an eine
    Stundenzahl.

    Der Preis ist eine schmale Stelle nach Mitternacht: Um 0:30 Uhr heißt
    etwas von 23 Uhr bereits „gestern“, obwohl es anderthalb Stunden her
    ist. Falsch ist das nicht — es WAR gestern —, und wer um halb eins
    arbeitet, ist die seltenere Hälfte.

    Diese Reihenfolge stand von Anfang an im Kommentar, war aber nicht so
    gebaut: Die Stundenabfrage lief vorher und fing den Fall ab. Aufgefallen
    ist es erst durch die Prüfung in `datum.test.js`.
  */
  const heuteMitternacht = new Date(jetzt);
  heuteMitternacht.setHours(0, 0, 0, 0);
  const damalsMitternacht = new Date(d);
  damalsMitternacht.setHours(0, 0, 0, 0);
  const tage = Math.round((heuteMitternacht - damalsMitternacht) / 86400000);

  if (tage === 0) {
    const stunden = Math.floor(minuten / 60);
    return stunden === 1 ? "vor einer Stunde" : `vor ${stunden} Stunden`;
  }
  if (tage === 1) return "gestern";
  if (tage < 7) return `vor ${tage} Tagen`;

  return kurzesDatum(d);
}
